/**
 * Small helpers for the attachments section (kept out of the component
 * file for react-refresh). Limits mirror the backend's settings.
 */

/** 1 GB per file (ATTACHMENTS_MAX_BYTES). */
export const MAX_ATTACHMENT_BYTES = 1024 ** 3;

/** 50 files per task (ATTACHMENTS_MAX_PER_TASK). */
export const MAX_ATTACHMENTS_PER_TASK = 50;

const UNITS = ["KB", "MB", "GB"];

/** "812 bytes", "4.2 KB", "18 MB", "1 GB" — one decimal under 10. */
export const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} ${bytes === 1 ? "byte" : "bytes"}`;
  let value = bytes;
  let unit = "";
  for (const u of UNITS) {
    value /= 1024;
    unit = u;
    if (value < 1024 || u === "GB") break;
  }
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ${unit}`;
};

const EXTENSION_KIND = {
  pdf: "document",
  doc: "document",
  docx: "document",
  odt: "document",
  rtf: "document",
  txt: "text",
  md: "text",
  csv: "sheet",
  xls: "sheet",
  xlsx: "sheet",
  ods: "sheet",
  zip: "archive",
  gz: "archive",
  tar: "archive",
  "7z": "archive",
  rar: "archive",
  mp3: "audio",
  wav: "audio",
  flac: "audio",
  ogg: "audio",
  m4a: "audio",
  mp4: "video",
  mov: "video",
  webm: "video",
  mkv: "video",
};

/**
 * Which icon a file gets: image | video | audio | document | sheet |
 * archive | text | file. The server's sniffed type wins; the extension
 * fills in where a sniffed type is generic (a .docx sniffs as a zip).
 */
export const fileKind = (name = "", contentType = "") => {
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (contentType.startsWith("image/")) return "image";
  if (contentType.startsWith("video/")) return "video";
  if (contentType.startsWith("audio/")) return "audio";
  if (contentType === "application/pdf") return "document";
  if (EXTENSION_KIND[ext]) return EXTENSION_KIND[ext];
  if (contentType.startsWith("text/")) return "text";
  return "file";
};

/**
 * What an upload failure says, and where Retry resumes. `step` is the
 * step that failed: "start" (asking mono), "upload" (the PUT to storage),
 * "complete" (asking mono to publish). `messageOf(err, fallback)` reads a
 * DRF error (apiErrorMessage). `rowSurvives` = the server still holds a
 * pending row that a restart should discard.
 *
 * @returns {{ message: string, retryable: boolean, resumeFrom: "start"|"upload"|"complete", rowSurvives?: boolean }}
 */
export const describeUploadFailure = (step, err, messageOf) => {
  const status = err?.response?.status;
  const offline = !err?.response;
  if (step === "start") {
    if (offline) {
      return {
        message: "Couldn't reach mono — check your connection and retry.",
        retryable: true,
        resumeFrom: "start",
      };
    }
    // 400 (size, the per-task cap), 403, 404: retrying can't help.
    return {
      message: messageOf(err, "Couldn't start the upload."),
      retryable: status >= 500,
      resumeFrom: "start",
    };
  }
  if (step === "upload") {
    if (offline) {
      return {
        message: "The file didn't reach storage — check your connection and retry.",
        retryable: true,
        resumeFrom: "upload",
      };
    }
    return {
      message: `Storage refused the file (HTTP ${status}) — the upload link may have expired. Retry starts it over.`,
      retryable: true,
      resumeFrom: "start",
      rowSurvives: true,
    };
  }
  if (offline || status >= 500) {
    return {
      message:
        "Couldn't confirm the upload — Retry checks again without sending the file twice.",
      retryable: true,
      resumeFrom: "complete",
    };
  }
  if (status === 409) {
    return {
      message: "The file never arrived in storage. Retry sends it again.",
      retryable: true,
      resumeFrom: "upload",
    };
  }
  if (status === 400) {
    // Size mismatch: the server already discarded the row.
    return {
      message: `${messageOf(err, "The stored file didn't match.")} Retry starts over.`
        .replace("Start the upload again. Retry starts over.", "Retry starts over."),
      retryable: true,
      resumeFrom: "start",
      rowSurvives: false,
    };
  }
  return {
    message: messageOf(err, "Upload failed."),
    retryable: false,
    resumeFrom: "complete",
  };
};

// Up to this size the photo viewer swaps the thumbnail for the original
// once it has loaded; bigger originals stay a click away ("Download
// original"), so stepping through photos never pulls 40 MB per step.
export const FULL_VIEW_MAX_BYTES = 15 * 1024 * 1024;

// An armed "Remove for everyone?" confirm disarms itself after this long.
export const DISARM_MS = 5000;
